import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { getAccountEmail } from '@/auth/accountEmail';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { ErrorScreen } from '@/components/ErrorScreen';
import { LoadingScreen } from '@/components/LoadingScreen';
import { getSafeReturnTo, withReturnTo } from '@/routes/returnTo';

import '@/features/marketing/marketing-tokens.css';

import {
  createProfileService,
  isProfileComplete,
} from './profileService';
import type { ProfileData } from './profileTypes';
import { profileQueryKey } from './profileTypes';
import styles from './ProfileSetupPage.module.css';

interface SetupDraft {
  firstName: string;
  instituteOfficialId: string;
  isInstructor: boolean;
  lastName: string;
}

interface SetupValidationErrors {
  firstName?: string;
  lastName?: string;
}

function createDataError(message: string) {
  return new Error(message);
}

function trimDraft(draft: SetupDraft): SetupDraft {
  return {
    firstName: draft.firstName.trim(),
    instituteOfficialId: draft.instituteOfficialId.trim(),
    isInstructor: draft.isInstructor,
    lastName: draft.lastName.trim(),
  };
}

function validateDraft(draft: SetupDraft): SetupValidationErrors {
  const trimmed = trimDraft(draft);
  const errors: SetupValidationErrors = {};

  if (!trimmed.firstName) {
    errors.firstName = 'First name is required.';
  }

  if (!trimmed.lastName) {
    errors.lastName = 'Last name is required.';
  }

  return errors;
}

export function ProfileSetupPage() {
  const { getIdToken, loading, signOut, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  // The account's email, or its Google sign-in's for a legacy account that
  // has no email of its own.
  const authEmail = getAccountEmail(user);
  const initializedDraftRef = useRef(false);
  const [draft, setDraft] = useState<SetupDraft>({
    firstName: '',
    instituteOfficialId: '',
    isInstructor: false,
    lastName: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const createClient = () => {
    if (!user) {
      throw createDataError('You must be signed in to complete your profile.');
    }

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: async () => {
        await signOut();
        navigate(withReturnTo('/login', '/profile/setup'), { replace: true });
      },
    });
  };

  const profileQuery = useQuery<ProfileData, Error>({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!kuduUserId) {
        throw createDataError('You must be signed in to complete your profile.');
      }

      return createProfileService(createClient()).getProfile({
        userId: kuduUserId,
      });
    },
    queryKey: profileQueryKey(kuduUserId),
    retry: false,
  });

  const profile = profileQuery.data ?? null;

  useEffect(() => {
    if (!profile || initializedDraftRef.current) {
      return;
    }

    initializedDraftRef.current = true;
    setDraft({
      firstName: profile.firstName,
      instituteOfficialId: profile.instituteOfficialId,
      isInstructor: profile.isInstructor,
      lastName: profile.lastName,
    });
  }, [profile]);

  useEffect(() => {
    if (profile && isProfileComplete(profile)) {
      navigate(returnTo, { replace: true });
    }
  }, [navigate, profile, returnTo]);

  const validationErrors = useMemo(() => validateDraft(draft), [draft]);
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const saveMutation = useMutation<ProfileData | null, Error, SetupDraft>({
    mutationFn: async (nextDraft) => {
      if (!kuduUserId || !authEmail) {
        throw createDataError('Profile data is not ready to save.');
      }

      return createProfileService(createClient()).updateProfile({
        profile: {
          firstName: nextDraft.firstName,
          instituteOfficialId: nextDraft.instituteOfficialId,
          isInstructor: nextDraft.isInstructor,
          lastName: nextDraft.lastName,
          preferredEmail: authEmail,
        },
        userId: kuduUserId,
      });
    },
    onSuccess: (updatedProfile, submittedDraft) => {
      if (!kuduUserId) {
        return;
      }

      const nextProfile =
        updatedProfile ??
        ({
          ...(profile ?? {
            courseIds: [],
            dismissedTours: [],
            loginEmail: authEmail,
            rawProfile: null,
            rawSettings: null,
            settings: { displayTheme: null },
            userId: kuduUserId,
          }),
          firstName: submittedDraft.firstName,
          instituteOfficialId: submittedDraft.instituteOfficialId,
          isInstructor: submittedDraft.isInstructor,
          lastName: submittedDraft.lastName,
          preferredEmail: authEmail,
        } satisfies ProfileData);

      queryClient.setQueryData(profileQueryKey(kuduUserId), nextProfile);
      navigate(returnTo, { replace: true });
    },
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setClientError(null);

    if (!authEmail) {
      setClientError('Your authenticated account does not include an email.');
      return;
    }

    const trimmedDraft = trimDraft(draft);
    const errors = validateDraft(trimmedDraft);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      await saveMutation.mutateAsync(trimmedDraft);
    } catch {
      // The mutation error is rendered below so the user can retry.
    }
  }

  if (loading || (Boolean(kuduUserId) && profileQuery.isPending)) {
    return <LoadingScreen message="Checking your profile..." />;
  }

  if (profileQuery.error) {
    return (
      <ErrorScreen
        message="Unable to load your profile setup state."
        onRetry={() => {
          void profileQuery.refetch();
        }}
      />
    );
  }

  const saveError = saveMutation.error?.message;
  const firstNameError = submitted ? validationErrors.firstName : undefined;
  const lastNameError = submitted ? validationErrors.lastName : undefined;
  const formError = clientError ?? saveError ?? null;

  return (
    <main className={styles.page}>
      <section aria-labelledby="profile-setup-title" className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title} id="profile-setup-title">
            Create your profile
          </h1>
          <p className={styles.description}>
            Add the basics Kudu needs before you enter your courses.
          </p>
        </div>

        <form className={styles.form} noValidate onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="setup-first-name">
              First name
            </label>
            <input
              aria-describedby={firstNameError ? 'setup-first-name-error' : undefined}
              aria-invalid={Boolean(firstNameError)}
              autoComplete="given-name"
              className={styles.input}
              id="setup-first-name"
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  firstName: event.target.value,
                }))
              }
              required
              value={draft.firstName}
            />
            {firstNameError ? (
              <p className={styles.error} id="setup-first-name-error">
                {firstNameError}
              </p>
            ) : null}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="setup-last-name">
              Last name
            </label>
            <input
              aria-describedby={lastNameError ? 'setup-last-name-error' : undefined}
              aria-invalid={Boolean(lastNameError)}
              autoComplete="family-name"
              className={styles.input}
              id="setup-last-name"
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  lastName: event.target.value,
                }))
              }
              required
              value={draft.lastName}
            />
            {lastNameError ? (
              <p className={styles.error} id="setup-last-name-error">
                {lastNameError}
              </p>
            ) : null}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="setup-email">
              Email
            </label>
            {/* The account's own address, which cannot change here. Text, so a
                long one wraps: a one-line field cut it off on a phone. */}
            <output className={styles.readOnlyValue} id="setup-email">
              {authEmail}
            </output>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="setup-institute-id">
              Student/Institute ID
            </label>
            <input
              autoComplete="off"
              className={styles.input}
              id="setup-institute-id"
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  instituteOfficialId: event.target.value,
                }))
              }
              value={draft.instituteOfficialId}
            />
            <p className={styles.helper}>Optional.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="setup-instructor">
              Are you an instructor?
            </label>
            <select
              className={styles.select}
              id="setup-instructor"
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  isInstructor: event.target.value === 'true',
                }))
              }
              value={draft.isInstructor ? 'true' : 'false'}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
            <p className={styles.helper}>
              This one-time setup choice is not editable from your normal
              profile page.
            </p>
          </div>

          <Button
            className={styles.submitButton}
            disabled={saveMutation.isPending || (submitted && hasValidationErrors)}
            fullWidth
            size="large"
            type="submit"
            variant="primary"
          >
            {saveMutation.isPending ? 'Saving profile...' : 'Continue'}
          </Button>
        </form>

        {formError ? (
          <p className={styles.error} role="alert">
            {formError}
          </p>
        ) : null}
      </section>
    </main>
  );
}
